import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Label } from '../../ui/label';
import { Loader } from '../../../ui_components/loader';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { emailRegex, positiveNumbersOneZeroRegex } from '../../../helper/regex';
import { Button } from '../../ui/button';
import { LucideX } from 'lucide-react';
import { ErrorInputPopUp } from '../../input_popups/error_input_popup';
import { updateAccount } from '../../../api/account';
import { toast } from 'sonner';
import { trackView } from '../../analytics/track_view';
import { MixpanelEvents } from '../../analytics/mix_panel_events';

type EstimateProps = {
  activeTab: boolean;
  loading: boolean;
  disabled: boolean;
  accountId: any;
  accountIsOwner: boolean;
  accountIsAdmin: boolean;
  formFields: any;
  estimateFields: any;
  handleRefetchAccount?(): void;
  handleActivateTab(tab: number): void;
};

type FormErrorsProps = {
  estimateExpiration: { active: boolean; message: string };
  additionalCcEmail: { active: boolean; message: string };
  additionalReplyToEmail: { active: boolean; message: string };
};

const normalizeEstimateTaxSelection = (value: string) => {
  if (value === 'excluded') return 'exclude';
  if (value === 'calculated') return 'calculate';
  return value || 'exclude';
};

const getFilledStatus = (value: string) => (String(value || '').trim() ? 'filled' : 'empty');

// todo:
// let bundleComponents = [
//   { id: 1, label: 'Main Fabric', value: 'main-fabric' },
//   { id: 2, label: 'Linings', value: 'linings' },
//   { id: 3, label: 'Embellishments', value: 'embellishments' },
//   {
//     id: 4,
//     label: 'Hardware / Headrail / Parts & Supplies',
//     value: 'hardware-headrail-partsAndSupplies',
//   },
//   { id: 5, label: 'Shipping & Handling', value: 'shipping-and-handling' },
// ];

const Estimate = (props: EstimateProps) => {
  // @ts-ignore
  const {
    loading,
    disabled,
    activeTab,
    accountId,
    formFields,
    accountIsOwner,
    accountIsAdmin,
    handleRefetchAccount,
    estimateFields,
    handleActivateTab,
  } = props;
  const clientFacingEmail = formFields?.clientFacingInfo?.email || '';

  const estimateExpirationRef = useRef<any>(null);
  const additionalCcEmailRef = useRef<any>(null);
  const additionalReplyToEmailRef = useRef<any>(null);

  const [estimateBundling, setEstimateBundling] = useState<string>('bundle');
  const [checkedBundles, setCheckedBundles] = useState<any[]>([]);
  const [estimateTaxDefaults, setEstimateTaxDefaults] = useState<string>('excluded');
  const [expirationDays, setExpirationDays] = useState<string>('30');
  const [defaultPaymentTerms, setDefaultPaymentTerms] = useState<string>('');
  const [defaultTermsAndConditions, setDefaultTermsAndConditions] = useState<string>('');
  const [additionalCcEmail, setAdditionalCcEmail] = useState<string>('');
  const [additionalCcEmails, setAdditionalCcEmails] = useState<string[]>([]);
  const [additionalReplyToEmail, setAdditionalReplyToEmail] = useState<string>('');
  const [additionalReplyToEmails, setAdditionalReplyToEmails] = useState<string[]>([]);
  const [accountEstimateSending, setAccountEstimateSending] = useState<{
    cc: { sender: boolean; clientFacing: boolean };
    replyTo: { sender: boolean; clientFacing: boolean };
  }>({
    cc: { sender: true, clientFacing: false },
    replyTo: { sender: true, clientFacing: false },
  });
  const [formErrors, setFormErrors] = useState<FormErrorsProps>({
    estimateExpiration: { active: false, message: '' },
    additionalCcEmail: { active: false, message: '' },
    additionalReplyToEmail: { active: false, message: '' },
  });
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    const handlePrefill = () => {
      setEstimateBundling((prev) => estimateFields?.estimateBundling || prev || 'bundle');
      setCheckedBundles((prev) => estimateFields?.estimateBundlingItems || prev || []);
      setEstimateTaxDefaults((prev) => estimateFields?.taxDefault || prev || 'excluded');
      setExpirationDays((prev) => estimateFields?.estimateExpirationDays || prev || '30');
      setDefaultPaymentTerms((prev) => estimateFields?.defaultPaymentTerms || prev || '');
      setDefaultTermsAndConditions(
        (prev) => estimateFields?.defaultTermsAndConditions || prev || ''
      );
      setAdditionalCcEmails((prev) => estimateFields?.additionalCcEmails || prev || []);
      setAdditionalReplyToEmails((prev) => estimateFields?.additionalReplyToEmails || prev || []);
      setAccountEstimateSending(
        (prev) =>
          estimateFields?.accountEstimateSending ||
          prev || {
            cc: { sender: true, clientFacing: false },
            replyTo: { sender: true, clientFacing: false },
          }
      );
    };

    handlePrefill();
  }, [estimateFields]);

  const handleSaving = (val: boolean) => {
    setSaving(val);
  };

  // *** START EMAILS ***
  const handleIncludesComma = (val: string) => {
    return val.includes(',');
  };

  const handleValidateEmails = (value: string) => {
    const items = value
      .split(',')
      .map((i) => i.trim())
      .filter((i) => i);
    if (items?.some((i) => !emailRegex.test(i))) {
      return false;
    }
    return true;
  };

  const handleSanitizeEmail = (val: string) => {
    const valArray = val
      .split(',')
      .map((i) => i.trim())
      .filter((i) => i && emailRegex.test(i));

    return [...new Set(valArray)];
  };

  const handleSetError = (name: string, active: boolean = false, message: string = '') => {
    if (!name) return;
    setFormErrors((prev) => {
      return { ...prev, [name]: { active, message } };
    });
  };

  // Validate if the client facing email is checked
  const handleValidateClientFacingEmailActive = (pos: 'cc' | 'replyTo', value: string): boolean => {
    let checkCase = accountEstimateSending?.[pos]?.clientFacing;

    if (checkCase && value === clientFacingEmail) {
      let msgPos = pos === 'cc' ? 'CC' : pos === 'replyTo' ? 'Reply-To' : '';
      let errMsg = `This email is already listed in ${msgPos}`;
      toast.error(errMsg);
      return false;
    }

    return true;
  };

  // *** CC EMAIL ***
  const handleAdditionalCcEmail = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    handleSetError('additionalCcEmail', false, '');
    setAdditionalCcEmail(value);

    const hasComma = handleIncludesComma(value);
    if (hasComma) {
      if (!handleValidateClientFacingEmailActive('cc', value.replace(',', ''))) {
        setAdditionalCcEmail('');
        return;
      }

      if (!handleValidateEmails(value)) {
        handleSetError('additionalCcEmail', true, 'Invalid email');
        return;
      }
      handleSetError('additionalCcEmail', false, '');

      const newValues = handleSanitizeEmail(value);
      setAdditionalCcEmails((prev) => {
        const payloadValues = newValues?.filter((i) => !prev.includes(i));
        return [...prev, ...payloadValues];
      });
      setAdditionalCcEmail('');
      return;
    }
  };

  const handleAdditionalCcEmails = () => {
    if (!handleValidateClientFacingEmailActive('cc', additionalCcEmail)) {
      setAdditionalCcEmail('');
      return;
    }

    if (!additionalCcEmail.trim() || additionalCcEmails.includes(additionalCcEmail)) {
      setAdditionalCcEmail('');
      return;
    }
    if (!emailRegex.test(additionalCcEmail)) {
      handleSetError('additionalCcEmail', true, 'Invalid email');
      return;
    }
    handleSetError('additionalCcEmail', false, '');
    setAdditionalCcEmails((prev) => {
      return [...prev, additionalCcEmail];
    });
    setAdditionalCcEmail('');
  };

  const handleDeleteAdditionalCcEmail = (email: string) => {
    setAdditionalCcEmails((prev) => {
      return prev.filter((i) => i !== email);
    });
  };
  // *** REPLY TO EMAIL ***
  const handleAdditionalReplyToEmail = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    handleSetError('additionalReplyToEmail', false, '');
    setAdditionalReplyToEmail(value);

    const hasComma = handleIncludesComma(value);
    if (hasComma) {
      if (!handleValidateClientFacingEmailActive('replyTo', value.replace(',', ''))) {
        setAdditionalReplyToEmail('');
        return;
      }

      if (!handleValidateEmails(value)) {
        handleSetError('additionalReplyToEmail', true, 'Invalid email');
        return;
      }
      handleSetError('additionalReplyToEmail', false, '');

      const newValues = handleSanitizeEmail(value);
      setAdditionalReplyToEmails((prev) => {
        const payloadValues = newValues?.filter((i) => !prev.includes(i));
        return [...prev, ...payloadValues];
      });
      setAdditionalReplyToEmail('');
      return;
    }
  };

  const handleAdditionalReplyToEmails = () => {
    if (!handleValidateClientFacingEmailActive('replyTo', additionalReplyToEmail)) {
      setAdditionalReplyToEmail('');
      return
    };

    if (
      !additionalReplyToEmail.trim() ||
      additionalReplyToEmails.includes(additionalReplyToEmail)
    ) {
      setAdditionalReplyToEmail('');
      return;
    }
    if (!emailRegex.test(additionalReplyToEmail)) {
      handleSetError('additionalReplyToEmail', true, 'Invalid email');
      return;
    }
    handleSetError('additionalReplyToEmail', false, '');
    setAdditionalReplyToEmails((prev) => {
      return [...prev, additionalReplyToEmail];
    });
    setAdditionalReplyToEmail('');
  };

  const handleDeleteAdditionalReplyToEmail = (email: string) => {
    setAdditionalReplyToEmails((prev) => {
      return prev.filter((i) => i !== email);
    });
  };
  // *** END EMAILS ***

  const handleDefaultPaymentTerms = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    setDefaultPaymentTerms(value);
  };

  const handleDefaultTermsAndConditions = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    setDefaultTermsAndConditions(value);
  };

  // todo:
  // const handleChooseBundles = (item: any) => {
  //   setCheckedBundles((prev) => {
  //     let foundItem = prev?.find((i) => i.id === item?.id);
  //     if (foundItem) {
  //       return prev?.filter((f) => f.id !== item?.id);
  //     }
  //     return [...prev, item];
  //   });
  // };

  const handleExpirationDays = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value && !positiveNumbersOneZeroRegex.test(value)) return;
    handleSetError('estimateExpiration', false, '');
    setExpirationDays(value);
  };

  const handleValidateEstimateExpiration = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const nVal = Number(value);
    if (!value || isNaN(nVal) || nVal <= 0) {
      handleSetError('estimateExpiration', true, 'Minimum value of 1');
    }
  };

  const handleAccountEstimateSending = (val: string | boolean, type: string) => {
    setAccountEstimateSending((prev) => {
      return { ...prev, [type]: { ...prev?.[type], clientFacing: val } };
    });
  };

  const handleOpenMyShopSettings = (item: string) => {
    window.open(`/myshop?item=${item}`, '_blank');
  };

  const handleValidateEstimateTab = () => {
    const validationFields = {
      estimateExpiration: (val: string) => {
        let num = Number(val);
        if (!val || isNaN(num) || num <= 0) return 'Minimum value of 1';
        return '';
      },
    };

    let nextErrors = {};

    for (const key of Object.keys(validationFields)) {
      const message = validationFields[key](expirationDays);
      nextErrors[key] = { active: !!message, message };
    }

    Object.entries(nextErrors).forEach(([key, value]: any) => {
      if (key === 'estimateExpiration' && value?.active) {
        if (estimateExpirationRef.current) {
          estimateExpirationRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
    });

    setFormErrors((prev) => ({ ...prev, ...nextErrors }));
    return Object.values(nextErrors).every((i: any) => !i.active);
  };

  const handleSaveEstimateTab = async () => {
    const validate = handleValidateEstimateTab();
    if (!validate) {
      console.error('Form not valid');
      return;
    }

    handleSaving(true);
    const payload = {
      accountId: accountId,
      targetAccountId: accountId,
      estimateBundling: estimateBundling,
      estimateBundlingItems: checkedBundles,
      taxDefault: estimateTaxDefaults,
      estimateExpirationDays: isNaN(Number(expirationDays)) ? 30 : Number(expirationDays),
      defaultPaymentTerms: defaultPaymentTerms,
      defaultTermsAndConditions: defaultTermsAndConditions,
      accountEstimateSending: accountEstimateSending,
      additionalCcEmails: additionalCcEmails,
      additionalReplyToEmails: additionalReplyToEmails,
    };
    const res = await updateAccount(payload);
    if (res) {
      trackView(MixpanelEvents.ACCOUNT_SETTINGS_ESTIMATES_SAVE, {
        tax_selection: normalizeEstimateTaxSelection(estimateTaxDefaults),
        estimate_expiration: payload.estimateExpirationDays,
        payment_terms: getFilledStatus(defaultPaymentTerms),
        terms_and_conditions: getFilledStatus(defaultTermsAndConditions),
      });
      toast('Settings saved', {
        description: 'Account settings have been updated.',
      });
      if (handleRefetchAccount) handleRefetchAccount();
      setTimeout(() => {
        handleSaving(false);
      }, 1000);
    }
  };

  return (
    <div className={`space-y-6 ${activeTab ? 'block' : 'hidden'}`}>
      {/* Estimate Bundling */}
      {/* todo: */}
      {/* <div className="space-y-4 p-[16px] bg-muted/50 rounded-lg">
        <h3 className="m-0 text-[16px] font-semibold text-foreground">Estimate Bundling</h3>
        <p className="text-sm text-muted-foreground">
          Choose whether to bundle components on estimates.
        </p>

        {!accountIsOwner && !accountIsAdmin ? (
          <div className="space-y-1">
            <div className="space-y-2">
              <Label className="text-sm leading-normal">Mode</Label>
              <p className="text-sm text-foreground py-2 min-h-[2.5rem] flex items-center">
                {estimateBundling === 'no-bundle' ? 'No Bundling' : 'Bundle Components'}
              </p>
            </div>
            {estimateBundling === 'bundle' && (
              <div className="pl-2 space-y-1">
                {checkedBundles && checkedBundles?.length > 0 ? (
                  checkedBundles?.map((i: any, idx: number) => {
                    return (
                      <p
                        key={idx}
                        className="m-0 mt-[4px] text-sm text-foreground"
                      >{`• ${i.label}`}</p>
                    );
                  })
                ) : (
                  <p className="m-0 text-sm text-muted-foreground">
                    No components selected for bundling
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <RadioGroup
                value={estimateBundling}
                onValueChange={setEstimateBundling}
                aria-readonly={false}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    disabled={disabled || saving}
                    aria-readonly={false}
                    value="no-bundle"
                    id="no-bundle"
                    data-qa="no-bundle"
                  />
                  <Label
                    data-disabled={disabled || saving}
                    className="data-[disabled=false]:cursor-pointer data-[disabled=true]:cursor-auto font-normal"
                    htmlFor="no-bundle"
                  >
                    <span className="text-sm text-foreground">No Bundling</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    disabled={disabled || saving}
                    aria-readonly={false}
                    value="bundle"
                    id="bundle"
                    data-qa="bundle"
                  />
                  <Label
                    data-disabled={disabled || saving}
                    className="data-[disabled=false]:cursor-pointer data-[disabled=true]:cursor-auto font-normal"
                    htmlFor="bundle"
                  >
                    <span className="text-sm text-foreground">
                      Bundle Components
                      <span className="text-muted-foreground font-normal">
                        (requires setup in your Quickbooks Online, for estimates in Quickbooks –
                        <a
                          className="text-primary inline-block underline hover:text-primary/80"
                          href="https://quipa.crisp.help/en/article/qbo-bundling-setup-h3lrqd/?bust=1764782701259"
                          target="_blank"
                        >
                          view directions here
                        </a>
                        )
                      </span>
                    </span>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {estimateBundling === 'bundle' && (
              <div className="space-y-3 pl-6 pt-1">
                <p className="text-sm text-muted-foreground">
                  Select which components you want to bundle with the fabrication price. Components
                  that are not selected will be listed as a separate line item.
                </p>

                {bundleComponents?.map((item: any) => {
                  return (
                    <div key={item?.id} className="flex items-center space-x-2">
                      <Checkbox
                        className="border-primary"
                        id={`${item?.value}_${item?.id}`}
                        disabled={disabled || saving}
                        checked={checkedBundles?.some((i) => i?.id === item?.id)}
                        onCheckedChange={() => handleChooseBundles(item)}
                        data-qa={`estimate_bundle_option_${item?.value}_${item?.id}`}
                      />
                      <Label className="cursor-pointer" htmlFor={`${item?.value}_${item?.id}`}>
                        {item?.label || ''}
                      </Label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div> */}
      {/* Item Descriptions */}
      <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
        <h3 className="m-0 text-[16px] font-semibold text-foreground">Item Descriptions</h3>
        <p className="text-sm text-muted-foreground">
          Customer-facing descriptions that appear on estimates are configured in each item's
          fabrication or component settings:
        </p>
        <ul className="space-y-2 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-muted-foreground">•</span>
            <span>
              <span className="font-medium text-foreground">Drapery</span>
              <span className="text-muted-foreground"> — set in </span>
              <button
                className={`data-[disabled=false]:text-primary data-[disabled=true]:text-muted-foreground data-[disabled=false]:hover:underline data-[disabled=false]:font-medium`}
                type="button"
                data-disabled={!accountIsOwner && !accountIsAdmin}
                disabled={!accountIsOwner && !accountIsAdmin}
                onClick={() => handleOpenMyShopSettings('drapery-item')}
                data-qa={`estimate_tab_item_description_li_drapery`}
              >
                Drapery Fabrication Settings
              </button>
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-muted-foreground">•</span>
            <span>
              <span className="font-medium text-foreground">Roman Shades</span>
              <span className="text-muted-foreground"> — set in </span>
              <button
                className={`data-[disabled=false]:text-primary data-[disabled=true]:text-muted-foreground data-[disabled=false]:hover:underline data-[disabled=false]:font-medium`}
                type="button"
                data-disabled={!accountIsOwner && !accountIsAdmin}
                disabled={!accountIsOwner && !accountIsAdmin}
                onClick={() => handleOpenMyShopSettings('roman-shade-item')}
                data-qa={`estimate_tab_item_description_li_roman_shades`}
              >
                Roman Shades Fabrication Settings
              </button>
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-muted-foreground">•</span>
            <span>
              <span className="font-medium text-foreground">Custom Items</span>
              <span className="text-muted-foreground"> — follow Drapery Fabrication Settings</span>
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-muted-foreground">•</span>
            <span>
              <span className="font-medium text-foreground">Components</span>
              <span className="text-muted-foreground"> — set in </span>
              <button
                className="text-primary hover:underline font-medium"
                type="button"
                // onClick={() => handleOpenMyShopSettings('component-item')}
                onClick={() => handleActivateTab(3)}
                data-qa={`estimate_tab_item_description_li_custom_items`}
              >
                Description Settings
              </button>
            </span>
          </li>
        </ul>
      </div>
      {/* Tax Defaults */}
      <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
        <h3 className="m-0 text-[16px] font-semibold text-foreground">Tax Defaults</h3>
        <p className="text-sm text-muted-foreground">Default tax setting for new documents.</p>
        {!accountIsOwner && !accountIsAdmin ? (
          <div className="space-y-2">
            <Label className="text-sm leading-normal">Tax Mode</Label>
            <p className="text-sm text-foreground py-2 min-h-[2.5rem] flex items-center">
              {estimateTaxDefaults === 'excluded'
                ? 'Excludes taxes'
                : estimateTaxDefaults === 'manual'
                  ? 'Enter tax amount manually'
                  : estimateTaxDefaults === 'calculated'
                    ? "Calculate from client's tax rate"
                    : ''}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <RadioGroup
              value={estimateTaxDefaults}
              onValueChange={setEstimateTaxDefaults}
              aria-readonly={false}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  disabled={disabled || saving}
                  aria-readonly={false}
                  value="excluded"
                  id="excluded"
                  data-qa="excluded"
                />
                <Label
                  data-disabled={disabled || saving}
                  className="data-[disabled=false]:cursor-pointer data-[disabled=true]:cursor-auto font-normal"
                  htmlFor="excluded"
                >
                  <span className="text-sm text-foreground">Estimates exclude taxes</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  disabled={disabled || saving}
                  aria-readonly={false}
                  value="manual"
                  id="manual"
                  data-qa="manual"
                />
                <Label
                  data-disabled={disabled || saving}
                  className="data-[disabled=false]:cursor-pointer data-[disabled=true]:cursor-auto font-normal"
                  htmlFor="manual"
                >
                  <span className="text-sm text-foreground">Enter tax amount manually</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  disabled={disabled || saving}
                  aria-readonly={false}
                  value="calculated"
                  id="calculated"
                  data-qa="calculated"
                />
                <Label
                  data-disabled={disabled || saving}
                  className="data-[disabled=false]:cursor-pointer data-[disabled=true]:cursor-auto font-normal"
                  htmlFor="calculated"
                >
                  <span className="text-sm text-foreground">
                    Calculate from client's tax rate and taxable items
                  </span>
                </Label>
              </div>
            </RadioGroup>
          </div>
        )}
      </div>
      {/* Estimate Expiration */}
      <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
        <h3 className="m-0 text-[16px] font-semibold text-foreground">Estimate Expiration</h3>
        <p className="text-sm text-muted-foreground">
          Default number of days until an Estimate expires from its document date.
        </p>
        {!accountIsOwner && !accountIsAdmin ? (
          <div className="space-y-2">
            <Label className="text-sm leading-normal">Expiration</Label>
            <p className="text-sm text-foreground py-2 min-h-[2.5rem] flex items-center">
              {expirationDays} days
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-2 max-w-xs">
            <div ref={estimateExpirationRef} className="relative">
              <Input
                value={expirationDays}
                onChange={handleExpirationDays}
                onBlur={handleValidateEstimateExpiration}
                placeholder="30"
                data-qa={`estimate_expiration_days`}
                className={`w-[80px] ${formErrors?.estimateExpiration?.active ? 'bg-errorBg text-error border-error' : ''}`}
                disabled={disabled || saving}
              />
              <ErrorInputPopUp
                anchorRef={estimateExpirationRef}
                active={formErrors?.estimateExpiration?.active}
                message={formErrors?.estimateExpiration?.message}
                position="top-start"
              />
            </div>
            <span className="text-sm text-muted-foreground">Days</span>
          </div>
        )}
      </div>
      {/* Default payment terms */}
      <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
        <h3 className="m-0 text-[16px] font-semibold text-foreground">Default Payment Terms</h3>
        <p className="text-sm text-muted-foreground">
          This text will appear as the default "Payment Terms" section on Estimates. You can
          override it per document.
        </p>
        {!accountIsOwner && !accountIsAdmin ? (
          <p className="text-sm text-foreground py-2 min-h-[2.5rem] flex items-center">
            {defaultPaymentTerms || 'No default set'}
          </p>
        ) : (
          <Textarea
            data-qa={`default_payment_terms_textarea`}
            disabled={disabled || saving}
            value={defaultPaymentTerms}
            onChange={handleDefaultPaymentTerms}
            className="min-h-[100px]"
            placeholder="e.g. 50% deposit due upon signing. Remainder due within 30 days of installation."
          />
        )}
      </div>
      {/* Default terms and conditions */}
      <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
        <h3 className="m-0 text-[16px] font-semibold text-foreground">
          Default Terms & Conditions
        </h3>
        <p className="text-sm text-muted-foreground">
          This text will appear as the default "Terms & Conditions" section on Estimates. You can
          override it per document.
        </p>
        {!accountIsOwner && !accountIsAdmin ? (
          <p className="text-sm text-foreground py-2 min-h-[2.5rem] flex items-center">
            {defaultTermsAndConditions || 'No default set'}
          </p>
        ) : (
          <Textarea
            data-qa={`default_terms_and_conditions_textarea`}
            disabled={disabled || saving}
            value={defaultTermsAndConditions}
            onChange={handleDefaultTermsAndConditions}
            className="min-h-[100px]"
            placeholder="e.g. All sales are final. Changes to the scope of work may result in additional charges..."
          />
        )}
      </div>
      {/* Estimate sending */}
      <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
        <h3 className="m-0 text-[16px] font-semibold text-foreground">Estimate Sending</h3>
        <p className="text-sm text-muted-foreground">
          Configure default CC recipients and Reply-To behavior for estimate emails. These defaults
          can be overridden when sending.
        </p>
        {!accountIsOwner && !accountIsAdmin ? (
          <div className="space-y-2">
            <div className="space-y-2">
              <Label className="text-sm leading-normal">Default CC Recipitents</Label>
              <p className="text-sm text-foreground flex items-center">-Sender (always copied)</p>
              {accountEstimateSending?.cc?.clientFacing && (
                <p className="text-sm text-foreground flex items-center">-{clientFacingEmail}</p>
              )}
              {additionalCcEmails?.length > 0 &&
                additionalCcEmails?.map((i: any, idx: number) => {
                  return (
                    <p key={idx} className="text-sm text-foreground flex items-center">
                      -{i}
                    </p>
                  );
                })}
            </div>
            <div className="space-y-2">
              <Label className="text-sm leading-normal">Reply-To</Label>
              <p className="text-sm text-foreground flex items-center">-Sender (always included)</p>
              {accountEstimateSending?.replyTo?.clientFacing && (
                <p className="text-sm text-foreground flex items-center">-{clientFacingEmail}</p>
              )}
              {additionalReplyToEmails?.length > 0 &&
                additionalReplyToEmails?.map((i: any, idx: number) => {
                  return (
                    <p key={idx} className="text-sm text-foreground flex items-center">
                      -{i}
                    </p>
                  );
                })}
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="leading-normal">Default CC Recipients</Label>
              <p className="text-xs text-muted-foreground">
                These addresses will be automatically copied on estimate emails.
              </p>
              <div className="space-y-2 pl-2">
                <div>
                  <Label className="w-max cursor-pointer font-normal">
                    <Checkbox checked={accountEstimateSending?.cc?.sender} disabled />
                    <span className="text-sm text-muted-foreground">Sender (always copied)</span>
                  </Label>
                </div>
                <div>
                  <Label className="w-max cursor-pointer" htmlFor="client-facing-email-cc">
                    <Checkbox
                      checked={accountEstimateSending?.cc?.clientFacing}
                      onCheckedChange={(val) => handleAccountEstimateSending(val, 'cc')}
                      id="client-facing-email-cc"
                      data-qa={'client-facing-email-cc'}
                      disabled={disabled || saving}
                    />
                    Account Client-Facing Email {clientFacingEmail ? `(${clientFacingEmail})` : ''}
                  </Label>
                </div>
              </div>
              <div className="space-y-1.5 pt-2">
                <Label className="leading-normal" htmlFor="additional-cc-email">
                  Additional CC emails
                </Label>
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {additionalCcEmails?.length > 0 &&
                    additionalCcEmails?.map((i: any, idx: number) => {
                      return (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs text-foreground border border-border"
                        >
                          {i}
                          <button
                            type="button"
                            onClick={() => handleDeleteAdditionalCcEmail(i)}
                            className="text-muted-foreground hover:text-foreground"
                            data-qa={`delete_additional_cc_email_${i}`}
                            disabled={disabled || saving}
                          >
                            <LucideX className="lucide lucide-x h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                </div>
                <div ref={additionalCcEmailRef} className="relative">
                  <Input
                    data-qa={`additional_cc_email_input`}
                    id="additional-cc-email"
                    value={additionalCcEmail}
                    onChange={handleAdditionalCcEmail}
                    onBlur={handleAdditionalCcEmails}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAdditionalCcEmails();
                      }
                    }}
                    placeholder="Add email and press Enter..."
                    className={`min-h-[32px] ${formErrors?.additionalCcEmail?.active ? 'bg-errorBg text-error border-error' : ''}`}
                    disabled={disabled || saving}
                  />
                  <ErrorInputPopUp
                    anchorRef={additionalCcEmailRef}
                    active={formErrors?.additionalCcEmail?.active}
                    message={formErrors?.additionalCcEmail?.message}
                    position="top-start"
                  />
                </div>
              </div>
            </div>
            <div className="shrink-0 bg-border h-[1px] w-full" />
            <div className="space-y-2">
              <Label className="leading-normal">Reply-To</Label>
              <p className="text-xs text-muted-foreground">
                When a client replies to an estimate email, the reply will go to all selected
                addresses.
              </p>
              <div className="space-y-2 pl-2">
                <div>
                  <Label className="w-max cursor-pointer font-normal">
                    <Checkbox checked={accountEstimateSending?.replyTo?.sender} disabled />
                    <span className="text-sm text-muted-foreground">Sender (always included)</span>
                  </Label>
                </div>
                <div>
                  <Label className="w-max cursor-pointer" htmlFor="client-facing-email-reply-to">
                    <Checkbox
                      checked={accountEstimateSending?.replyTo?.clientFacing}
                      onCheckedChange={(val) => handleAccountEstimateSending(val, 'replyTo')}
                      id="client-facing-email-reply-to"
                      data-qa={'client-facing-email-reply-to'}
                      disabled={disabled || saving}
                    />
                    Account Client-Facing Email {clientFacingEmail ? `(${clientFacingEmail})` : ''}
                  </Label>
                </div>
              </div>
              <div className="space-y-1.5 pt-2">
                <Label className="leading-normal" htmlFor="additional-reply-to-email">
                  Additional Reply-To emails
                </Label>
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {additionalReplyToEmails?.length > 0 &&
                    additionalReplyToEmails?.map((i: any, idx: number) => {
                      return (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs text-foreground border border-border"
                        >
                          {i}
                          <button
                            type="button"
                            onClick={() => handleDeleteAdditionalReplyToEmail(i)}
                            className="text-muted-foreground hover:text-foreground"
                            data-qa={`delete_additional_reply_to_email_${i}`}
                            disabled={disabled || saving}
                          >
                            <LucideX className="lucide lucide-x h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                </div>
                <div ref={additionalReplyToEmailRef} className="relative">
                  <Input
                    data-qa={`additional_reply_to_email_input`}
                    id="additional-reply-to-email"
                    value={additionalReplyToEmail}
                    onChange={handleAdditionalReplyToEmail}
                    onBlur={handleAdditionalReplyToEmails}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAdditionalReplyToEmails();
                      }
                    }}
                    placeholder="Add email and press Enter..."
                    className={`min-h-[32px] ${formErrors?.additionalReplyToEmail?.active ? 'bg-errorBg text-error border-error' : ''}`}
                    disabled={disabled || saving}
                  />
                  <ErrorInputPopUp
                    anchorRef={additionalReplyToEmailRef}
                    active={formErrors?.additionalReplyToEmail?.active}
                    message={formErrors?.additionalReplyToEmail?.message}
                    position="top-start"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* ACTIONS */}
      {(accountIsOwner || accountIsAdmin) && !disabled && (
        <div className="flex justify-end">
          <Button
            data-qa={`save_account_estimate_tab_button`}
            disabled={disabled || saving}
            onClick={handleSaveEstimateTab}
            type="button"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      )}

      {(loading || saving) && (
        <Loader className="fixed top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2" />
      )}
    </div>
  );
};

export default Estimate;
